import { Link, useParams } from 'react-router-dom'

import { getBlogCategoryLabel, getBlogPostDateLabel, getBlogReadTimeLabel, normalizeBlogContentHtml } from '@/data/blog'
import { getBlogAuthor } from '@/data/blog-authors'

import { BlogPostCard } from '@/components/blog/blog-post-card'
import { RouteNotFoundState } from '@/components/route-states'
import { JsonLd } from '@/components/seo/json-ld'
import { SeoHead } from '@/components/seo/seo-head'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { getPublicBlogArticle } from '@/lib/blog/server'
import { cn } from '@/lib/utils'
import { createMetadata, getBlogPostingSchema, getBreadcrumbSchema, getFaqSchema, withArticleExtras } from '@/lib/seo'

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>()
  const result = slug ? getPublicBlogArticle(slug) : null

  if (!result) {
    return (
      <RouteNotFoundState
        title="This article isn't available."
        description="It may have moved. Browse the blog for the latest posts."
        homeHref="/blog"
        homeLabel="Back to blog"
      />
    )
  }

  const article = result.post
  const author = getBlogAuthor(article.authorId)
  const relatedPosts = result.relatedPosts
  const contentHtml = normalizeBlogContentHtml(article.contentHtml)
  const baseMetadata = createMetadata({
    path: `/blog/${article.slug}`,
    title: article.seoTitle,
    description: article.description,
    keywords: [article.primaryKeyword, ...article.secondaryKeywords],
    imagePath: article.featuredImageUrl,
    openGraphType: 'article',
  })
  const metadata = withArticleExtras(baseMetadata, {
    publishedTime: article.publishedAt,
    modifiedTime: article.updatedAt,
    authors: [author.name],
    tags: [article.primaryKeyword, ...article.secondaryKeywords],
    imageAlt: article.featuredImageAlt,
    imageWidth: 1600,
    imageHeight: 900,
  })

  return (
    <article className="section-shell pb-20 pt-12">
      <SeoHead metadata={metadata} />
      <JsonLd
        data={getBlogPostingSchema({
          title: article.title,
          description: article.description,
          path: `/blog/${article.slug}`,
          publishedAt: article.publishedAt,
          updatedAt: article.updatedAt,
          author,
          keywords: [article.primaryKeyword, ...article.secondaryKeywords],
          image: article.featuredImageUrl,
        })}
      />
      <JsonLd
        data={getBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: article.title, path: `/blog/${article.slug}` },
        ])}
      />
      {article.faqItems.length > 0 ? <JsonLd data={getFaqSchema(article.faqItems)} /> : null}

      <div className="mx-auto max-w-[1460px] space-y-10">
        <nav className="flex flex-wrap items-center gap-2 text-sm text-ink-muted">
          <Link to="/blog" className="transition hover:text-ink">
            Blog
          </Link>
          <span>/</span>
          <span className="text-ink">{article.title}</span>
        </nav>

        <section className="overflow-hidden rounded-[36px] border border-line bg-white shadow-soft">
          <div className="space-y-0">
            <div className="space-y-7 p-8 sm:p-10 xl:p-12">
              <div className="flex flex-wrap items-center gap-3">
                <Badge>{getBlogCategoryLabel(article.category)}</Badge>
                <span className="text-xs font-medium uppercase tracking-[0.14em] text-ink-muted">
                  {getBlogReadTimeLabel(article)}
                </span>
              </div>

              <div className="space-y-5">
                <h1 className="max-w-5xl text-balance text-[2.6rem] font-semibold leading-[1.02] sm:text-[3.4rem] lg:text-[4.15rem]">
                  {article.title}
                </h1>
                <p className="max-w-4xl text-xl leading-9 text-ink-soft">{article.excerpt}</p>
              </div>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-ink-muted">
                <Link
                  to={`/editorial-standards#${author.id}`}
                  className="font-medium transition hover:text-brand"
                >
                  By {author.name}
                </Link>
                <span>Published {getBlogPostDateLabel(article)}</span>
                {article.updatedAt !== article.publishedAt ? (
                  <span>Updated {getBlogPostDateLabel({ publishedAt: article.updatedAt })}</span>
                ) : null}
              </div>

              <div className="rounded-[28px] border border-line bg-[linear-gradient(180deg,rgba(248,250,255,0.96),rgba(255,255,255,0.96))] p-6 sm:p-7">
                <h2 className="text-xl font-semibold leading-[1.2] text-ink">Quick answer</h2>
                <ul className="mt-5 grid gap-3 md:grid-cols-3">
                  {article.summaryPoints.map((point) => (
                    <li key={point} className="rounded-[22px] border border-line bg-white px-5 py-5 text-base leading-7 text-ink">
                      {point}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="max-w-5xl space-y-6 text-[1.14rem] leading-[2rem] text-ink">
                {article.intro.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>

            <div className="relative aspect-[16/7] min-h-[320px] border-t border-line sm:min-h-[420px] xl:min-h-[520px]">
              <img
                src={article.featuredImageUrl}
                alt={article.featuredImageAlt}
                className="object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        <div className="grid gap-8 xl:grid-cols-[minmax(0,1.28fr)_340px]">
          <div className="min-w-0 space-y-8">
            <section className="rounded-[32px] border border-line bg-white p-7 shadow-soft sm:p-10 xl:p-12">
              <div className="blog-prose" dangerouslySetInnerHTML={{ __html: contentHtml }} />
            </section>

            <section className="rounded-[32px] border border-line bg-white p-7 shadow-soft sm:p-10 xl:p-12">
              <h2 className="text-3xl font-semibold leading-[1.12] text-ink">Frequently asked questions</h2>
              <div className="mt-8 grid gap-5">
                {article.faqItems.map((item) => (
                  <div key={item.question} className="rounded-[24px] border border-line bg-surface-strong px-5 py-5">
                    <h3 className="text-xl font-semibold leading-[1.2] text-ink">{item.question}</h3>
                    <p className="mt-3 text-[1.06rem] leading-8 text-ink">{item.answer}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-[30px] border border-white/10 bg-night p-8 text-white shadow-glow sm:p-10">
              <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-end">
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-semibold text-white/80">
                    Product links
                  </div>
                  <h2 className="max-w-2xl text-3xl font-semibold leading-[1.16] text-white sm:text-4xl">
                    Use the research, then choose the right donation setup.
                  </h2>
                  <p className="max-w-2xl text-base leading-7 text-white/70">
                    Compare your options, then move into a donation setup your team can launch and maintain with confidence.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Button asChild>
                    <Link to="/features">View features</Link>
                  </Button>
                  <Button asChild variant="secondary">
                    <Link to="/pricing">View pricing</Link>
                  </Button>
                  <Button asChild variant="dark">
                    <Link to="/signup">Start free</Link>
                  </Button>
                </div>
              </div>
            </section>

            {relatedPosts.length > 0 ? (
              <section>
                <h2 className="text-3xl font-semibold leading-[1.12] text-ink">Related reading</h2>
                <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {relatedPosts.map((post) => (
                    <BlogPostCard key={post.slug} post={post} compact />
                  ))}
                </div>
              </section>
            ) : null}
          </div>

          <aside className="min-w-0 space-y-6 xl:sticky xl:top-24 xl:self-start">
            <Card className="p-6">
              <CardTitle className="text-xl">In this article</CardTitle>
              <CardDescription className="mt-3">
                Jump to the section you need.
              </CardDescription>
              <nav className="mt-6 space-y-3">
                {article.tableOfContents.map((item, index) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className={cn(
                      'flex items-start gap-3 rounded-2xl border border-line bg-surface-strong px-4 py-3 text-sm text-ink transition hover:border-brand/20 hover:bg-brand/5',
                    )}
                  >
                    <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-brand">
                      {index + 1}
                    </span>
                    <span>{item.title}</span>
                  </a>
                ))}
              </nav>
            </Card>

            <Card className="p-6">
              <CardTitle className="text-xl">Keep exploring</CardTitle>
              <CardDescription className="mt-3">
                Related product pages and next reads.
              </CardDescription>
              <div className="mt-6 space-y-3">
                {article.internalLinks.map((item) => (
                  <Link
                    key={`${item.href}-${item.label}`}
                    to={item.href}
                    className="flex items-center justify-between rounded-2xl border border-line bg-surface-strong px-4 py-3 text-sm font-semibold text-ink transition hover:border-brand/20 hover:bg-brand/5"
                  >
                    <span>{item.label}</span>
                    <span className="text-brand">Open</span>
                  </Link>
                ))}
              </div>
            </Card>
          </aside>
        </div>
      </div>
    </article>
  )
}

export const Component = BlogArticlePage
